import React, { useState, useEffect } from 'react';
import { ShoppingBag, ArrowLeft, ShieldCheck, Truck, RefreshCw, Sparkles } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API_BASE_URL = 'https://rudrakshenterprises.liveindiatech.com';

// Helper function to correctly format image URLs for live server and admin uploads
const getCorrectImageUrl = (url) => {
    if (!url) return 'https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&w=600&q=80';

    // Agar URL mein local IP hai, toh use live domain se replace karein
    if (url.includes('127.0.0.1:8000') || url.includes('localhost:8000')) {
        return url.replace(/https?:\/\/(127\.0\.0\.1|localhost):8000/, 'https://rudrakshenterprises.liveindiatech.com');
    }

    // Agar URL '/' se shuru hota hai, toh seedha domain ke sath jodein (bina /rudraksh_backend ke)
    if (url.startsWith('/')) {
        return `https://rudrakshenterprises.liveindiatech.com${url}`;
    }

    return url;
};

export default function ProductDetailsPage({
    product,
    cartCount,
    setIsCartOpen,
    setActiveCategory,
    setCurrentPage,
    addToCart,
    adminProducts,
    pujaProducts,
    herbalProducts,
    jaivikProducts,
    ayurvedicProducts,
    organicProducts,
    onViewDetails
}) {
    const [selectedImage, setSelectedImage] = useState('');

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (product) {
            const firstImg = product.image || (product.images && product.images.length > 0 ? product.images[0].image : '');
            setSelectedImage(getCorrectImageUrl(firstImg));
        }
    }, [product]);

    if (!product) {
        return (
            <div className="min-h-screen flex flex-col bg-white">
                <Navbar cartCount={cartCount} setIsCartOpen={setIsCartOpen} setActiveCategory={setActiveCategory} setCurrentPage={setCurrentPage} />
                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                    <h2 className="text-2xl font-black text-gray-800 mb-2">Product Not Found</h2>
                    <p className="text-xs text-gray-500 mb-6">Please go back and select a product to view its details.</p>
                    <button
                        onClick={() => setCurrentPage('home')}
                        className="bg-orange-600 text-white px-6 py-2.5 rounded-xl text-xs font-bold cursor-pointer border-none shadow-md hover:bg-orange-700 transition"
                    >
                        Back to Home
                    </button>
                </div>
                <Footer
                    setActiveCategory={setActiveCategory}
                    setCurrentPage={setCurrentPage}
                    adminProducts={adminProducts}
                    pujaProducts={pujaProducts}
                    herbalProducts={herbalProducts}
                    jaivikProducts={jaivikProducts}
                    ayurvedicProducts={ayurvedicProducts}
                    organicProducts={organicProducts}
                    onViewDetails={onViewDetails}
                />
            </div>
        );
    }

    // Process and fix gallery images with getCorrectImageUrl
    const galleryImages = [];
    if (product.image) galleryImages.push(getCorrectImageUrl(product.image));
    if (product.images && product.images.length > 0) {
        product.images.forEach(imgObj => {
            const imgPath = imgObj.image || imgObj;
            if (imgPath) {
                const fixedUrl = getCorrectImageUrl(imgPath);
                if (!galleryImages.includes(fixedUrl)) {
                    galleryImages.push(fixedUrl);
                }
            }
        });
    }

    const activeImagePreview = selectedImage ? getCorrectImageUrl(selectedImage) : (galleryImages[0] || 'https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&w=600&q=80');

    return (
        <div className="min-h-screen flex flex-col bg-white">
            <Navbar
                cartCount={cartCount}
                setIsCartOpen={setIsCartOpen}
                setActiveCategory={setActiveCategory}
                setCurrentPage={setCurrentPage}
                adminProducts={adminProducts}
                pujaProducts={pujaProducts}
                herbalProducts={herbalProducts}
                jaivikProducts={jaivikProducts}
                ayurvedicProducts={ayurvedicProducts}
                organicProducts={organicProducts}
                onViewDetails={onViewDetails}
            />

            <main className="flex-1 w-full px-4 sm:px-8 md:px-12 lg:px-20 py-12 bg-gray-50/50">
                {/* Back Button */}
                <button
                    onClick={() => {
                        if (typeof setCurrentPage === 'function') setCurrentPage('category');
                    }}
                    className="mb-8 flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-xl text-xs font-bold hover:bg-gray-100 transition shadow-sm cursor-pointer border-none"
                >
                    <ArrowLeft size={16} /> Back to Products
                </button>

                {/* Product Detail Card Container */}
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 sm:p-10 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-10 items-start">

                    {/* Left: Product Images */}
                    <div className="space-y-4">
                        <div className="bg-gray-50 rounded-3xl p-8 border border-gray-100 flex items-center justify-center relative overflow-hidden h-80 sm:h-96 shadow-inner">
                            <span className="absolute top-4 right-4 bg-orange-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow">
                                {product.discount || 'Off 10%'}
                            </span>
                            <img
                                src={activeImagePreview}
                                onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&w=600&q=80'; }}
                                alt={product.name}
                                className="w-full h-full object-contain max-h-80 drop-shadow-md rounded-2xl"
                            />
                        </div>

                        {/* Extra Gallery Image Thumbnails */}
                        {galleryImages.length > 1 && (
                            <div className="flex items-center gap-3 overflow-x-auto pb-2">
                                {galleryImages.map((imgUrl, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setSelectedImage(imgUrl)}
                                        className={`w-20 h-20 rounded-2xl border-2 overflow-hidden bg-gray-50 p-1 transition cursor-pointer flex-shrink-0 ${activeImagePreview === imgUrl ? 'border-orange-600 shadow-md ring-2 ring-orange-200' : 'border-gray-200 opacity-70 hover:opacity-100'}`}
                                    >
                                        <img src={imgUrl} alt={`thumb-${idx}`} className="w-full h-full object-contain rounded-xl" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Right: Product Info */}
                    <div className="space-y-6 text-left">
                        <div>
                            <span className="text-orange-600 text-xs font-bold uppercase tracking-wider flex items-center gap-1 mb-1">
                                <Sparkles size={14} /> {product.categoryName || 'Natural Wellness Product'}
                            </span>
                            <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 tracking-tight">
                                {product.name}
                            </h1>
                        </div>

                        <div className="flex items-center gap-4">
                            <span className="text-2xl sm:text-3xl font-black text-orange-600">₹{product.price}</span>
                            <span className="text-sm sm:text-base text-gray-400 line-through">₹{product.original_price || product.originalPrice}</span>
                            <span className="bg-green-50 text-green-700 text-xs font-bold px-2.5 py-1 rounded-lg">
                                In Stock & Ready
                            </span>
                        </div>

                        <p className="text-sm text-gray-600 leading-relaxed border-t border-b border-gray-100 py-4">
                            {product.description || 'Pure and traditional wellness product carefully sourced to maintain highest quality standards for your daily lifestyle.'}
                        </p>

                        {/* Feature Badges */}
                        <div className="grid grid-cols-3 gap-3 pt-2">
                            <div className="bg-gray-50 p-3 rounded-2xl text-center border border-gray-100">
                                <ShieldCheck className="mx-auto text-orange-600 mb-1" size={20} />
                                <span className="text-[10px] font-bold text-gray-700 block">100% Pure</span>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-2xl text-center border border-gray-100">
                                <Truck className="mx-auto text-orange-600 mb-1" size={20} />
                                <span className="text-[10px] font-bold text-gray-700 block">Fast Delivery</span>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-2xl text-center border border-gray-100">
                                <RefreshCw className="mx-auto text-orange-600 mb-1" size={20} />
                                <span className="text-[10px] font-bold text-gray-700 block">Easy Return</span>
                            </div>
                        </div>

                        {/* Add to Cart Button */}
                        <div className="pt-4">
                            <button
                                onClick={() => addToCart(product)}
                                className="w-full bg-orange-600 hover:bg-orange-700 text-white py-4 rounded-2xl text-sm font-bold transition flex items-center justify-center gap-2 shadow-lg cursor-pointer border-none"
                            >
                                <ShoppingBag size={18} /> Add to Cart
                            </button>
                        </div>
                    </div>

                </div>
            </main>

            <Footer
                setActiveCategory={setActiveCategory}
                setCurrentPage={setCurrentPage}
                adminProducts={adminProducts}
                pujaProducts={pujaProducts}
                herbalProducts={herbalProducts}
                jaivikProducts={jaivikProducts}
                ayurvedicProducts={ayurvedicProducts}
                organicProducts={organicProducts}
                onViewDetails={onViewDetails}
            />
        </div>
    );
}